import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOnceInView, useReveal } from "../hooks/useScroll";

/* The company's whole thesis in one picture: a crowd of people who touched
   your product, almost none of whom you can name.
 *
 * The proportion is on a slider the visitor drags, deliberately. We are not
 * going to put a conversion rate on a marketing page, and a figure somebody
 * sets themselves cannot be mistaken for one we are claiming. */

const COLS = 26;
const ROWS = 7;
const TOTAL = COLS * ROWS;

/* A fixed shuffle so the known ones scatter through the crowd instead of
   filling a block from the corner. Seeded, so it is the same picture on every
   load and in every browser. */
function shuffledOrder(n) {
  const a = Array.from({ length: n }, (_, i) => i);
  let seed = 20260809;
  for (let i = n - 1; i > 0; i -= 1) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const j = seed % (i + 1);
    const t = a[i];
    a[i] = a[j];
    a[j] = t;
  }
  return a;
}

export default function Gap() {
  const ref = useReveal();
  const [pct, setPct] = useState(0);
  const fieldRef = useRef(null);
  const order = useMemo(() => shuffledOrder(TOTAL), []);
  /* rank[i] is where dot i sits in the reveal order, so the flip is a lookup
     rather than a search through the shuffle on every frame */
  const rank = useMemo(() => {
    const r = new Array(TOTAL);
    order.forEach((dotIndex, position) => {
      r[dotIndex] = position;
    });
    return r;
  }, [order]);

  const paint = useCallback(
    (value) => {
      const field = fieldRef.current;
      if (!field) return;
      const cut = Math.round((value / 100) * TOTAL);
      const kids = field.children;
      for (let i = 0; i < kids.length; i += 1) {
        const on = rank[i] < cut;
        if (kids[i].dataset.on !== String(on)) kids[i].dataset.on = String(on);
      }
    },
    [rank]
  );

  useEffect(() => {
    paint(pct);
  }, [pct, paint]);

  /* a short unprompted sweep the first time it comes into view, so the idea
     lands even for somebody who never touches the control */
  const teasedRef = useRef(false);
  const tease = useCallback(() => {
    if (teasedRef.current) return;
    teasedRef.current = true;
    let t0 = null;
    const run = (t) => {
      if (t0 === null) t0 = t;
      const k = Math.min(1, (t - t0) / 1100);
      setPct(Math.round(k * 18));
      if (k < 1) requestAnimationFrame(run);
    };
    requestAnimationFrame(run);
  }, []);
  useOnceInView(ref, tease);

  const dots = useMemo(
    () =>
      Array.from({ length: TOTAL }, (_, i) => (
        <span key={i} className="gap__dot" data-on="false" style={{ "--d": `${(rank[i] % 40) * 9}ms` }} />
      )),
    [rank]
  );

  return (
    <section className="sec sec--pad gap" id="gap" ref={ref}>
      <div className="shell">
        <div className="gap__head rv">
          <div>
            <span className="eyebrow">The gap</span>
            <h2 className="h-lg" style={{ marginTop: 20 }}>
              Every one of them chose you.
            </h2>
          </div>
          <p className="lede">
            A month of people picking your product off a shelf, ordering it at a
            counter, being handed it at a stand. The dark ones are the people you
            will never be able to name, thank, ask, or invite back.
          </p>
        </div>

        <div className="gap__field rv" ref={fieldRef} aria-hidden="true"
          style={{ "--cols": COLS }}>
          {dots}
        </div>

        <div className="gap__ctrl rv">
          <div className="gap__legend">
            <span><i className="is-off" />Anonymous</span>
            <span><i className="is-on" />Known, consented, yours</span>
          </div>

          <label className="gap__slider">
            <span className="micro">Drag it</span>
            <input
              type="range"
              min="0"
              max="100"
              value={pct}
              onChange={(e) => {
                teasedRef.current = true;
                setPct(Number(e.target.value));
              }}
              aria-label="Share of people you can name"
            />
            <b className="gap__read">{pct}%</b>
          </label>
        </div>

        <p className="gap__note rv">
          That number is yours to set, not ours to claim. What SipNScore decides
          is whether the moment is capturable at all, and in every one of those
          places, it now is.
        </p>
      </div>
    </section>
  );
}
